import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, Clock, ChevronDown, Volume2, VolumeX, Moon, Settings, Bell, BellOff, X, Play, Check } from 'lucide-react';

const PrayerTimesPage = () => {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [weeklyTimes, setWeeklyTimes] = useState([]);
  const [monthlyTimes, setMonthlyTimes] = useState([]);
  const [location, setLocation] = useState({ city: '', country: '', lat: null, lng: null });
  const [loading, setLoading] = useState(true);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [countdown, setCountdown] = useState({ hours: '00', minutes: '00', seconds: '00' });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('Türkiye');
  const [activeTab, setActiveTab] = useState('weekly');
  const [showSettings, setShowSettings] = useState(false);
  const [showAdhanModal, setShowAdhanModal] = useState(false);
  const [currentAdhan, setCurrentAdhan] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [showNotificationPermissionModal, setShowNotificationPermissionModal] = useState(false);
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] = useState(false);
  const [showPermissionSuccessModal, setShowPermissionSuccessModal] = useState(false);
  
  // Notification settings from localStorage
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('prayerPageSettings');
    return saved ? JSON.parse(saved) : {
      soundEnabled: true,
      notifyBefore5Min: true,
      notifyOnAdhan: true,
      playAdhanSound: true
    };
  });
  
  const audioRef = useRef(null);
  const warningAudioRef = useRef(null);
  const lastWarningPrayer = useRef(null);
  const lastAdhanPrayer = useRef(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningPrayer, setWarningPrayer] = useState(null);

  // Prayer names
  const prayerNames = {
    Fajr: 'İmsak',
    Sunrise: 'Güneş',
    Dhuhr: 'Öğle',
    Asr: 'İkindi',
    Maghrib: 'Akşam',
    Isha: 'Yatsı'
  };

  // Sound URLs
  const ADHAN_URL = 'https://customer-assets.emergentagent.com/job_reciterlib/artifacts/umhpyrt5_Ezan.mp3';
  const WARNING_SOUND_URL = '/namaz_vakit_bildirim.mp3'; // 5 dakika uyarı sesi

  // Countries and cities - TÜRKİYE'NİN TÜM 81 İLİ
  const countries = {
    'Türkiye': [
      { name: 'Adana', lat: 37.0000, lng: 35.3213, method: 13 },
      { name: 'Adıyaman', lat: 37.7648, lng: 38.2786, method: 13 },
      { name: 'Afyonkarahisar', lat: 38.7507, lng: 30.5567, method: 13 },
      { name: 'Ağrı', lat: 39.7191, lng: 43.0503, method: 13 },
      { name: 'Aksaray', lat: 38.3687, lng: 34.0370, method: 13 },
      { name: 'Amasya', lat: 40.6499, lng: 35.8353, method: 13 },
      { name: 'Ankara', lat: 39.9334, lng: 32.8597, method: 13 },
      { name: 'Antalya', lat: 36.8969, lng: 30.7133, method: 13 },
      { name: 'Ardahan', lat: 41.1105, lng: 42.7022, method: 13 },
      { name: 'Artvin', lat: 41.1828, lng: 41.8183, method: 13 },
      { name: 'Aydın', lat: 37.8560, lng: 27.8416, method: 13 },
      { name: 'Balıkesir', lat: 39.6484, lng: 27.8826, method: 13 },
      { name: 'Bartın', lat: 41.6344, lng: 32.3375, method: 13 },
      { name: 'Batman', lat: 37.8812, lng: 41.1351, method: 13 },
      { name: 'Bayburt', lat: 40.2552, lng: 40.2249, method: 13 },
      { name: 'Bilecik', lat: 40.0567, lng: 30.0665, method: 13 },
      { name: 'Bingöl', lat: 38.8854, lng: 40.4966, method: 13 },
      { name: 'Bitlis', lat: 38.4006, lng: 42.1095, method: 13 },
      { name: 'Bolu', lat: 40.7359, lng: 31.6082, method: 13 },
      { name: 'Burdur', lat: 37.7203, lng: 30.2906, method: 13 },
      { name: 'Bursa', lat: 40.1885, lng: 29.0610, method: 13 },
      { name: 'Çanakkale', lat: 40.1553, lng: 26.4142, method: 13 },
      { name: 'Çankırı', lat: 40.6013, lng: 33.6134, method: 13 },
      { name: 'Çorum', lat: 40.5506, lng: 34.9556, method: 13 },
      { name: 'Denizli', lat: 37.7765, lng: 29.0864, method: 13 },
      { name: 'Diyarbakır', lat: 37.9144, lng: 40.2306, method: 13 },
      { name: 'Düzce', lat: 40.8438, lng: 31.1565, method: 13 },
      { name: 'Edirne', lat: 41.6818, lng: 26.5623, method: 13 },
      { name: 'Elazığ', lat: 38.6810, lng: 39.2264, method: 13 },
      { name: 'Erzincan', lat: 39.7500, lng: 39.5000, method: 13 },
      { name: 'Erzurum', lat: 39.9000, lng: 41.2700, method: 13 },
      { name: 'Eskişehir', lat: 39.7767, lng: 30.5206, method: 13 },
      { name: 'Gaziantep', lat: 37.0662, lng: 37.3833, method: 13 },
      { name: 'Giresun', lat: 40.9128, lng: 38.3895, method: 13 },
      { name: 'Gümüşhane', lat: 40.4386, lng: 39.5086, method: 13 },
      { name: 'Hakkari', lat: 37.5833, lng: 43.7333, method: 13 },
      { name: 'Hatay', lat: 36.4018, lng: 36.3498, method: 13 },
      { name: 'Iğdır', lat: 39.9167, lng: 44.0333, method: 13 },
      { name: 'Isparta', lat: 37.7648, lng: 30.5566, method: 13 },
      { name: 'İstanbul', lat: 41.0082, lng: 28.9784, method: 13 },
      { name: 'İzmir', lat: 38.4237, lng: 27.1428, method: 13 },
      { name: 'Kahramanmaraş', lat: 37.5858, lng: 36.9371, method: 13 },
      { name: 'Karabük', lat: 41.2061, lng: 32.6204, method: 13 },
      { name: 'Karaman', lat: 37.1759, lng: 33.2287, method: 13 },
      { name: 'Kars', lat: 40.6167, lng: 43.1000, method: 13 },
      { name: 'Kastamonu', lat: 41.3887, lng: 33.7827, method: 13 },
      { name: 'Kayseri', lat: 38.7312, lng: 35.4787, method: 13 },
      { name: 'Kırıkkale', lat: 39.8468, lng: 33.5153, method: 13 },
      { name: 'Kırklareli', lat: 41.7333, lng: 27.2167, method: 13 },
      { name: 'Kırşehir', lat: 39.1425, lng: 34.1709, method: 13 },
      { name: 'Kilis', lat: 36.7184, lng: 37.1212, method: 13 },
      { name: 'Kocaeli', lat: 40.8533, lng: 29.8815, method: 13 },
      { name: 'Konya', lat: 37.8746, lng: 32.4932, method: 13 },
      { name: 'Kütahya', lat: 39.4167, lng: 29.9833, method: 13 },
      { name: 'Malatya', lat: 38.3552, lng: 38.3095, method: 13 },
      { name: 'Manisa', lat: 38.6191, lng: 27.4289, method: 13 },
      { name: 'Mardin', lat: 37.3212, lng: 40.7245, method: 13 },
      { name: 'Mersin', lat: 36.8121, lng: 34.6415, method: 13 },
      { name: 'Muğla', lat: 37.2153, lng: 28.3636, method: 13 },
      { name: 'Muş', lat: 38.9462, lng: 41.7539, method: 13 },
      { name: 'Nevşehir', lat: 38.6939, lng: 34.6857, method: 13 },
      { name: 'Niğde', lat: 37.9667, lng: 34.6833, method: 13 },
      { name: 'Ordu', lat: 40.9839, lng: 37.8764, method: 13 },
      { name: 'Osmaniye', lat: 37.0742, lng: 36.2478, method: 13 },
      { name: 'Rize', lat: 41.0201, lng: 40.5234, method: 13 },
      { name: 'Sakarya', lat: 40.6940, lng: 30.4358, method: 13 },
      { name: 'Samsun', lat: 41.2867, lng: 36.3300, method: 13 },
      { name: 'Siirt', lat: 37.9333, lng: 41.9500, method: 13 },
      { name: 'Sinop', lat: 42.0231, lng: 35.1531, method: 13 },
      { name: 'Sivas', lat: 39.7477, lng: 37.0179, method: 13 },
      { name: 'Şanlıurfa', lat: 37.1591, lng: 38.7969, method: 13 },
      { name: 'Şırnak', lat: 37.4187, lng: 42.4918, method: 13 },
      { name: 'Tekirdağ', lat: 40.9833, lng: 27.5167, method: 13 },
      { name: 'Tokat', lat: 40.3167, lng: 36.5500, method: 13 },
      { name: 'Trabzon', lat: 41.0027, lng: 39.7168, method: 13 },
      { name: 'Tunceli', lat: 39.1079, lng: 39.5401, method: 13 },
      { name: 'Uşak', lat: 38.6823, lng: 29.4082, method: 13 },
      { name: 'Van', lat: 38.4891, lng: 43.4089, method: 13 },
      { name: 'Yalova', lat: 40.6500, lng: 29.2667, method: 13 },
      { name: 'Yozgat', lat: 39.8181, lng: 34.8147, method: 13 },
      { name: 'Zonguldak', lat: 41.4564, lng: 31.7987, method: 13 },
    ],
    'İsveç': [
      { name: 'Stockholm', lat: 59.3293, lng: 18.0686, method: 3 },
      { name: 'Göteborg', lat: 57.7089, lng: 11.9746, method: 3 },
      { name: 'Malmö', lat: 55.6050, lng: 13.0038, method: 3 },
      { name: 'Uppsala', lat: 59.8586, lng: 17.6389, method: 3 },
      { name: 'Västerås', lat: 59.6099, lng: 16.5448, method: 3 },
      { name: 'Örebro', lat: 59.2753, lng: 15.2134, method: 3 },
    ],
    'Almanya': [
      { name: 'Berlin', lat: 52.5200, lng: 13.4050, method: 3 },
      { name: 'München', lat: 48.1351, lng: 11.5820, method: 3 },
      { name: 'Hamburg', lat: 53.5511, lng: 9.9937, method: 3 },
      { name: 'Köln', lat: 50.9375, lng: 6.9603, method: 3 },
      { name: 'Frankfurt', lat: 50.1109, lng: 8.6821, method: 3 },
      { name: 'Düsseldorf', lat: 51.2277, lng: 6.7735, method: 3 },
    ],
    'Fransa': [
      { name: 'Paris', lat: 48.8566, lng: 2.3522, method: 3 },
      { name: 'Lyon', lat: 45.7640, lng: 4.8357, method: 3 },
      { name: 'Marsilya', lat: 43.2965, lng: 5.3698, method: 3 },
      { name: 'Toulouse', lat: 43.6047, lng: 1.4442, method: 3 },
    ],
    'Hollanda': [
      { name: 'Amsterdam', lat: 52.3676, lng: 4.9041, method: 3 },
      { name: 'Rotterdam', lat: 51.9244, lng: 4.4777, method: 3 },
      { name: 'Lahey', lat: 52.0705, lng: 4.3007, method: 3 },
      { name: 'Utrecht', lat: 52.0907, lng: 5.1214, method: 3 },
    ],
    'Belçika': [
      { name: 'Brüksel', lat: 50.8503, lng: 4.3517, method: 3 },
      { name: 'Antwerp', lat: 51.2194, lng: 4.4025, method: 3 },
      { name: 'Gent', lat: 51.0543, lng: 3.7174, method: 3 },
    ],
    'İngiltere': [
      { name: 'Londra', lat: 51.5074, lng: -0.1278, method: 3 },
      { name: 'Birmingham', lat: 52.4862, lng: -1.8904, method: 3 },
      { name: 'Manchester', lat: 53.4808, lng: -2.2426, method: 3 },
      { name: 'Leeds', lat: 53.8008, lng: -1.5491, method: 3 },
    ],
    'Avusturya': [
      { name: 'Viyana', lat: 48.2082, lng: 16.3738, method: 3 },
      { name: 'Graz', lat: 47.0707, lng: 15.4395, method: 3 },
    ],
    'İsviçre': [
      { name: 'Zürich', lat: 47.3769, lng: 8.5417, method: 3 },
      { name: 'Cenevre', lat: 46.2044, lng: 6.1432, method: 3 },
      { name: 'Basel', lat: 47.5596, lng: 7.5886, method: 3 },
    ],
    'Danimarka': [
      { name: 'Kopenhag', lat: 55.6761, lng: 12.5683, method: 3 },
      { name: 'Aarhus', lat: 56.1629, lng: 10.2039, method: 3 },
    ],
    'Norveç': [
      { name: 'Oslo', lat: 59.9139, lng: 10.7522, method: 3 },
      { name: 'Bergen', lat: 60.3913, lng: 5.3221, method: 3 },
    ],
    'İtalya': [
      { name: 'Roma', lat: 41.9028, lng: 12.4964, method: 3 },
      { name: 'Milano', lat: 45.4642, lng: 9.1900, method: 3 },
      { name: 'Napoli', lat: 40.8518, lng: 14.2681, method: 3 },
    ],
    'İspanya': [
      { name: 'Madrid', lat: 40.4168, lng: -3.7038, method: 3 },
      { name: 'Barselona', lat: 41.3851, lng: 2.1734, method: 3 },
      { name: 'Valencia', lat: 39.4699, lng: -0.3763, method: 3 },
    ],
  };

  // İstanbul'u varsayılan şehir olarak bul
  const defaultCity = countries['Türkiye'].find(city => city.name === 'İstanbul') || countries['Türkiye'][0];
  const [selectedCity, setSelectedCity] = useState(defaultCity);

  // Days and months
  const daysOfWeek = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('prayerPageSettings', JSON.stringify(settings));
  }, [settings]);

  // Register Service Worker and setup background notifications
  useEffect(() => {
    let swRegistration = null;
    
    // Register Service Worker for background notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
          swRegistration = registration;
          
          // Update service worker if new version available
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
              }
            });
          });
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
      
      // Listen for messages from Service Worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('Message from SW:', event.data);
        if (event.data?.type === 'PLAY_ADHAN') {
          // Play adhan sound when notification is clicked
          playAdhanFromNotification(event.data.prayer, event.data.location);
        }
      });
    }

    // Check notification permission and show modal if needed
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'default') {
        // Show custom permission modal after a short delay
        setTimeout(() => {
          setShowNotificationPermissionModal(true);
        }, 2000);
      }
    }
    
    // Check URL params for auto-play adhan (when opened from notification)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('playAdhan') === 'true') {
      const prayer = urlParams.get('prayer') || 'Namaz';
      setTimeout(() => {
        playAdhanFromNotification(prayer, `${selectedCity?.name || 'İstanbul'}, ${selectedCountry}`);
      }, 1000);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Schedule notifications when prayer times are loaded
  useEffect(() => {
    if (prayerTimes && notificationPermission === 'granted' && 'serviceWorker' in navigator) {
      schedulePrayerNotifications();
    }
  }, [prayerTimes, notificationPermission, settings, selectedCity, selectedCountry]);

  // Schedule prayer notifications via Service Worker
  const schedulePrayerNotifications = async () => {
    if (!prayerTimes || !navigator.serviceWorker.controller) return;
    
    try {
      navigator.serviceWorker.controller.postMessage({
        type: 'SCHEDULE_PRAYER_NOTIFICATIONS',
        prayerTimes: prayerTimes.timings,
        location: `${selectedCity.name}, ${selectedCountry}`,
        settings: {
          notifyBefore5Min: settings.notifyBefore5Min,
          notifyOnAdhan: settings.notifyOnAdhan,
          soundEnabled: settings.soundEnabled,
          playAdhanSound: settings.playAdhanSound
        }
      });
      console.log('Prayer notifications scheduled');
    } catch (error) {
      console.error('Error scheduling notifications:', error);
    }
  };

  // Play adhan when notification is clicked
  const playAdhanFromNotification = (prayer, location) => {
    setCurrentAdhan({ prayer, location });
    setShowAdhanModal(true);
    
    if (settings.playAdhanSound && audioRef.current) {
      audioRef.current.src = ADHAN_URL;
      audioRef.current.play().catch(err => console.log('Audio error:', err));
    }
  };

  // Handle notification permission request - Improved version
  const handleNotificationPermission = async (allow) => {
    setShowNotificationPermissionModal(false);
    
    if (!allow) {
      setNotificationPermission('denied');
      return;
    }
    
    if ('Notification' in window) {
      try {
        // Request permission from browser
        const permission = await Notification.requestPermission();
        console.log('Notification permission result:', permission);
        
        // Update state immediately
        setNotificationPermission(permission);
        
        // Save to localStorage for persistence
        localStorage.setItem('notificationPermission', permission);
        
        if (permission === 'granted') {
          // Show success modal
          setShowPermissionSuccessModal(true);
          
          // Show browser notification too
          setTimeout(() => {
            new Notification('🕌 Bildirimler Aktif!', {
              body: 'Namaz vakitlerinden önce hatırlatma alacaksınız.',
              icon: '/moon-icon.png',
              tag: 'permission-granted',
              requireInteraction: false
            });
          }, 500);
          
          // Schedule notifications if prayer times are loaded
          if (prayerTimes) {
            setTimeout(() => {
              schedulePrayerNotifications();
              console.log('Notifications scheduled after permission granted');
            }, 1000);
          }
        } else if (permission === 'denied') {
          // Show denied modal instead of alert
          setShowPermissionDeniedModal(true);
        }
      } catch (error) {
        console.error('Notification permission error:', error);
        setShowPermissionDeniedModal(true);
      }
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  // Check and sync notification permission on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && 'Notification' in window) {
        const currentPermission = Notification.permission;
        if (currentPermission !== notificationPermission) {
          setNotificationPermission(currentPermission);
          localStorage.setItem('notificationPermission', currentPermission);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check on focus
    const handleFocus = () => {
      if ('Notification' in window) {
        const currentPermission = Notification.permission;
        setNotificationPermission(currentPermission);
      }
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [notificationPermission]);

  // Helper function to clean prayer time format (remove timezone info like "+03")
  const cleanTimeFormat = (time) => {
    if (!time) return '--:--';
    // Remove timezone info like " (UTC)", " (+03)", etc.
    return time.replace(/\s*\([^)]*\)/g, '').trim();
  };

  // Fetch prayer times
  const fetchPrayerTimes = async (lat, lng, method = 13) => {
    try {
      // Doğrudan Aladhan API'sini kullan - backend'e bağımlılığı kaldır
      const response = await fetch(
        `https://api.aladhan.com/v1/timings?latitude=${lat}&longitude=${lng}&method=${method}`
      );
      const apiData = await response.json();
      
      if (apiData.code === 200) {
        const timings = apiData.data.timings;
        const dateInfo = apiData.data.date;
        
        const data = {
          success: true,
          timings: {
            Fajr: timings.Fajr || "",
            Sunrise: timings.Sunrise || "",
            Dhuhr: timings.Dhuhr || "",
            Asr: timings.Asr || "",
            Maghrib: timings.Maghrib || "",
            Isha: timings.Isha || ""
          },
          date: {
            readable: dateInfo.readable || "",
            hijri: dateInfo.hijri?.date || "",
            hijri_month: dateInfo.hijri?.month?.en || "",
            hijri_day: dateInfo.hijri?.day || "",
            hijri_year: dateInfo.hijri?.year || ""
          }
        };
        
        setPrayerTimes(data);
        calculateNextPrayer(data.timings);
      }
    } catch (err) {
      console.error('Prayer times error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch weekly prayer times
  const fetchWeeklyTimes = async (lat, lng, method = 13) => {
    try {
      const weekly = [];
      const today = new Date();
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        const dateStr = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
        
        const response = await fetch(
          `https://api.aladhan.com/v1/timings/${dateStr}?latitude=${lat}&longitude=${lng}&method=${method}`
        );
        const data = await response.json();
        
        if (data.code === 200) {
          weekly.push({
            date: date,
            hijri: data.data.date.hijri,
            timings: data.data.timings
          });
        }
      }
      
      setWeeklyTimes(weekly);
    } catch (err) {
      console.error('Weekly times error:', err);
    }
  };

  // Fetch monthly prayer times
  const fetchMonthlyTimes = async (lat, lng, method = 13) => {
    try {
      setLoadingMonthly(true);
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      
      const response = await fetch(
        `https://api.aladhan.com/v1/calendar/${year}/${month}?latitude=${lat}&longitude=${lng}&method=${method}`
      );
      const data = await response.json();
      
      if (data.code === 200) {
        const monthly = data.data.map((dayData, index) => ({
          date: new Date(year, month - 1, index + 1),
          hijri: dayData.date.hijri,
          timings: dayData.timings
        }));
        setMonthlyTimes(monthly);
      }
    } catch (err) {
      console.error('Monthly times error:', err);
    } finally {
      setLoadingMonthly(false);
    }
  };

  // Calculate next prayer
  const calculateNextPrayer = useCallback((timings) => {
    if (!timings) return;

    const now = new Date();
    const prayers = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
    
    for (const prayer of prayers) {
      const [hours, minutes] = timings[prayer].split(':').map(Number);
      const prayerTime = new Date();
      prayerTime.setHours(hours, minutes, 0, 0);
      
      if (prayerTime > now) {
        setNextPrayer({ name: prayer, time: timings[prayer], date: prayerTime });
        return;
      }
    }
    
    // Next is tomorrow's Fajr
    const [hours, minutes] = timings.Fajr.split(':').map(Number);
    const tomorrowFajr = new Date();
    tomorrowFajr.setDate(tomorrowFajr.getDate() + 1);
    tomorrowFajr.setHours(hours, minutes, 0, 0);
    setNextPrayer({ name: 'Fajr', time: timings.Fajr, date: tomorrowFajr });
  }, []);

  // Play adhan - shows modal and plays sound
  const playAdhan = useCallback((prayerName) => {
    const prayerTr = prayerNames[prayerName] || prayerName;
    
    // Show modal
    setCurrentAdhan({
      prayer: prayerTr,
      location: `${selectedCity.name}, ${selectedCountry}`
    });
    setShowAdhanModal(true);

    // Play ezan sound
    if (audioRef.current && settings.soundEnabled && settings.playAdhanSound) {
      audioRef.current.src = ADHAN_URL;
      audioRef.current.play().catch(err => console.log('Audio play error:', err));
    }

    // Send notification via Service Worker (works in background)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        title: `🕌 ${selectedCity.name} - ${prayerTr} Ezanı`,
        body: `${selectedCity.name} için ${prayerTr} ezanı vakti geldi! Haydi namaza!`,
        prayer: prayerTr,
        location: `${selectedCity.name}, ${selectedCountry}`,
        soundType: 'adhan',
        playSound: settings.playAdhanSound
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      // Fallback to direct notification
      new Notification(`🕌 ${selectedCity.name} - ${prayerTr} Ezanı`, {
        body: `${selectedCity.name} için ${prayerTr} ezanı vakti geldi!`,
        icon: '/moon-icon.png',
        tag: 'adhan-notification',
        requireInteraction: true,
        silent: false
      });
    }
  }, [selectedCity, selectedCountry, settings]);

  // Show 5 min warning with modal and sound
  const showWarning = useCallback((prayerName) => {
    const prayerTr = prayerNames[prayerName] || prayerName;
    
    // Show warning modal
    setWarningPrayer({
      prayer: prayerTr,
      prayerKey: prayerName,
      location: `${selectedCity.name}, ${selectedCountry}`
    });
    setShowWarningModal(true);
    
    // Play warning sound (namaz_vakit_bildirim.mp3)
    if (settings.soundEnabled && warningAudioRef.current) {
      warningAudioRef.current.src = WARNING_SOUND_URL;
      warningAudioRef.current.play().catch(err => console.log('Warning audio error:', err));
    }
    
    // Send notification via Service Worker (works in background even when tab is closed)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        title: `⏰ ${selectedCity.name} - ${prayerTr} Namazı`,
        body: `${prayerTr} ezanına 5 dakika kaldı! Abdest alıp namaza hazırlanın.`,
        prayer: prayerTr,
        location: `${selectedCity.name}, ${selectedCountry}`,
        soundType: 'warning',
        playSound: settings.soundEnabled
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      // Fallback to direct notification
      const notification = new Notification(`⏰ ${selectedCity.name} - ${prayerTr} Namazı`, {
        body: `${prayerTr} ezanına 5 dakika kaldı!`,
        icon: '/moon-icon.png',
        tag: 'warning-notification',
        requireInteraction: true,
        silent: false
      });

      notification.onclick = () => {
        window.focus();
        setShowWarningModal(true);
        notification.close();
      };
    }
  }, [selectedCity, selectedCountry, settings]);

  // Close warning modal
  const closeWarningModal = () => {
    setShowWarningModal(false);
    setWarningPrayer(null);
    if (warningAudioRef.current) {
      warningAudioRef.current.pause();
      warningAudioRef.current.currentTime = 0;
    }
  };

  // Close adhan modal
  const closeAdhanModal = () => {
    setShowAdhanModal(false);
    setCurrentAdhan(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Test 5-minute warning notification
  const testWarningNotification = () => {
    const prayerTr = prayerNames[nextPrayer?.name] || 'Öğle';
    
    // Show warning modal
    setWarningPrayer({
      prayer: prayerTr,
      prayerKey: nextPrayer?.name || 'Dhuhr',
      location: `${selectedCity.name}, ${selectedCountry}`
    });
    setShowWarningModal(true);
    
    // Play warning sound
    if (settings.soundEnabled && warningAudioRef.current) {
      warningAudioRef.current.src = WARNING_SOUND_URL;
      warningAudioRef.current.play().catch(err => console.log('Warning audio error:', err));
    }
    
    // Test browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`🕌 ${selectedCity.name} - ${prayerTr} Namazı`, {
        body: `${prayerTr} ezanına 5 dakika kaldı! (Test)`,
        icon: '/moon-icon.png',
        tag: 'test-warning-notification',
        requireInteraction: true
      });
    }
  };

  // Test adhan notification
  const testNotification = () => {
    // Test browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`🕌 Test Bildirimi - ${selectedCity.name}`, {
        body: `${prayerNames[nextPrayer?.name] || 'Öğle'} ezanı test bildirimi`,
        icon: '/moon-icon.png',
        tag: 'test-notification'
      });
    }

    // Play test sound
    if (settings.soundEnabled && settings.playAdhanSound) {
      if (audioRef.current) {
        audioRef.current.src = ADHAN_URL;
        audioRef.current.play().catch(err => console.log('Audio error:', err));
        // Stop after 5 seconds for test
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        }, 5000);
      }
    }

    // Show test modal
    setCurrentAdhan({
      prayer: 'Test',
      location: `${selectedCity.name}, ${selectedCountry}`
    });
    setShowAdhanModal(true);
  };

  // Update countdown and check for notifications
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
      
      if (nextPrayer?.date) {
        const now = new Date();
        const diff = nextPrayer.date - now;
        
        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          
          setCountdown({
            hours: hours.toString().padStart(2, '0'),
            minutes: minutes.toString().padStart(2, '0'),
            seconds: seconds.toString().padStart(2, '0')
          });

          // 5 minute warning
          if (diff <= 5 * 60 * 1000 && diff > 4.9 * 60 * 1000 && settings.notifyBefore5Min) {
            if (lastWarningPrayer.current !== nextPrayer.name) {
              lastWarningPrayer.current = nextPrayer.name;
              showWarning(nextPrayer.name);
            }
          }
        } else {
          // Prayer time reached
          if (settings.notifyOnAdhan) {
            if (lastAdhanPrayer.current !== nextPrayer.name) {
              lastAdhanPrayer.current = nextPrayer.name;
              playAdhan(nextPrayer.name);
            }
          }
          // Recalculate
          if (prayerTimes?.timings) {
            setTimeout(() => calculateNextPrayer(prayerTimes.timings), 1000);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [nextPrayer, prayerTimes, settings, calculateNextPrayer, playAdhan, showWarning]);

  // Load data on mount and city change
  useEffect(() => {
    setLoading(true);
    lastWarningPrayer.current = null;
    lastAdhanPrayer.current = null;
    fetchPrayerTimes(selectedCity.lat, selectedCity.lng, selectedCity.method);
    fetchWeeklyTimes(selectedCity.lat, selectedCity.lng, selectedCity.method);
    fetchMonthlyTimes(selectedCity.lat, selectedCity.lng, selectedCity.method);
    setLocation({ city: selectedCity.name, country: selectedCountry });
  }, [selectedCity, selectedCountry]);

  // Select country
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    // Türkiye için İstanbul'u, diğer ülkeler için ilk şehri seç
    if (country === 'Türkiye') {
      const istanbul = countries[country].find(c => c.name === 'İstanbul');
      setSelectedCity(istanbul || countries[country][0]);
    } else {
      setSelectedCity(countries[country][0]);
    }
    setShowCountryDropdown(false);
    setCitySearchQuery('');
  };

  // Select city
  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setShowCityDropdown(false);
    setCitySearchQuery('');
  };

  // Format date
  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} ${daysOfWeek[d.getDay()]}`;
  };

  // Moon phase - Custom icon image
  const getMoonPhase = () => {
    return (
      <img 
        src="/moon-icon.png" 
        alt="Ay Takvimi" 
        className="w-16 h-16 object-contain drop-shadow-lg"
        style={{ filter: 'drop-shadow(0 0 8px rgba(213, 196, 161, 0.5))' }}
      />
    );
  };

  // Toggle setting
  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#5C4033] to-[#3D2B1F]">
      {/* Hidden audio elements */}
      <audio ref={audioRef} preload="none" />
      <audio ref={warningAudioRef} preload="none" />

      {/* Header with dropdowns */}
      <div className="bg-[#5C4033] py-4 border-b border-[#8B7355]/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* Country Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setShowCountryDropdown(!showCountryDropdown); setShowCityDropdown(false); setShowSettings(false); }}
                className="bg-[#F5EFE7] text-[#5C4033] px-6 py-2.5 rounded-lg flex items-center gap-2 min-w-[180px] justify-between shadow-md hover:shadow-lg transition-shadow border border-[#D5C4A1]"
              >
                <span className="font-medium">{selectedCountry}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showCountryDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-[#F5EFE7] rounded-lg shadow-xl z-50 w-full max-h-72 overflow-y-auto border border-[#D5C4A1]">
                  {Object.keys(countries).map(country => (
                    <button
                      key={country}
                      onClick={() => handleCountrySelect(country)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-[#E8DCC8] transition-colors ${selectedCountry === country ? 'bg-[#D5C4A1] text-[#5C4033] font-medium' : 'text-[#5C4033]'}`}
                    >
                      {country}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* City Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setShowCityDropdown(!showCityDropdown); setShowCountryDropdown(false); setShowSettings(false); setCitySearchQuery(''); }}
                className="bg-[#F5EFE7] text-[#5C4033] px-6 py-2.5 rounded-lg flex items-center gap-2 min-w-[180px] justify-between shadow-md hover:shadow-lg transition-shadow border border-[#D5C4A1]"
              >
                <span className="font-medium">{selectedCity.name}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCityDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showCityDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-[#F5EFE7] rounded-lg shadow-xl z-50 w-64 border border-[#D5C4A1]">
                  {/* Search Input */}
                  <div className="p-2 border-b border-[#D5C4A1]">
                    <input
                      type="text"
                      placeholder="Şehir ara..."
                      value={citySearchQuery}
                      onChange={(e) => setCitySearchQuery(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-md border border-[#D5C4A1] focus:outline-none focus:ring-2 focus:ring-[#8B7355] bg-white text-[#5C4033]"
                      autoFocus
                    />
                  </div>
                  {/* City List */}
                  <div className="max-h-64 overflow-y-auto">
                    {countries[selectedCountry]
                      .filter(city => city.name.toLowerCase().includes(citySearchQuery.toLowerCase()))
                      .map(city => (
                        <button
                          key={city.name}
                          onClick={() => handleCitySelect(city)}
                          className={`w-full text-left px-4 py-2.5 hover:bg-[#E8DCC8] transition-colors ${selectedCity.name === city.name ? 'bg-[#D5C4A1] text-[#5C4033] font-medium' : 'text-[#5C4033]'}`}
                        >
                          {city.name}
                        </button>
                      ))}
                    {countries[selectedCountry].filter(city => city.name.toLowerCase().includes(citySearchQuery.toLowerCase())).length === 0 && (
                      <div className="px-4 py-3 text-sm text-[#8B7355] text-center">
                        Şehir bulunamadı
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Settings Button */}
            <button
              onClick={() => { setShowSettings(!showSettings); setShowCountryDropdown(false); setShowCityDropdown(false); }}
              className={`p-2.5 rounded-lg shadow-md transition-all border ${showSettings ? 'bg-[#8B7355] text-white border-[#8B7355]' : 'bg-[#F5EFE7] text-[#5C4033] border-[#D5C4A1] hover:bg-[#E8DCC8]'}`}
              title="Bildirim Ayarları"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="max-w-md mx-auto mt-4 bg-[#F5EFE7] rounded-xl shadow-xl p-4 border border-[#D5C4A1]">
              <h3 className="font-bold text-[#5C4033] mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#8B7355]" />
                Bildirim Ayarları
              </h3>
              
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-[#E8DCC8]">
                  <div className="flex items-center gap-3">
                    {settings.soundEnabled ? <Volume2 className="w-5 h-5 text-[#8B7355]" /> : <VolumeX className="w-5 h-5 text-gray-400" />}
                    <span className="text-[#5C4033]">Ses Açık</span>
                  </div>
                  <button
                    onClick={() => toggleSetting('soundEnabled')}
                    className={`w-12 h-6 rounded-full transition-colors ${settings.soundEnabled ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.soundEnabled ? 'translate-x-6' : ''}`} />
                  </button>
                </label>

                <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-[#E8DCC8]">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#8B7355]" />
                    <span className="text-[#5C4033]">5 Dakika Önce Uyarı</span>
                  </div>
                  <button
                    onClick={() => toggleSetting('notifyBefore5Min')}
                    className={`w-12 h-6 rounded-full transition-colors ${settings.notifyBefore5Min ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.notifyBefore5Min ? 'translate-x-6' : ''}`} />
                  </button>
                </label>

                <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-[#E8DCC8]">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-[#8B7355]" />
                    <span className="text-[#5C4033]">Ezan Vaktinde Bildirim</span>
                  </div>
                  <button
                    onClick={() => toggleSetting('notifyOnAdhan')}
                    className={`w-12 h-6 rounded-full transition-colors ${settings.notifyOnAdhan ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.notifyOnAdhan ? 'translate-x-6' : ''}`} />
                  </button>
                </label>

                <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-[#E8DCC8]">
                  <div className="flex items-center gap-3">
                    <Volume2 className="w-5 h-5 text-[#8B7355]" />
                    <span className="text-[#5C4033]">Ezan Sesi Çal</span>
                  </div>
                  <button
                    onClick={() => toggleSetting('playAdhanSound')}
                    className={`w-12 h-6 rounded-full transition-colors ${settings.playAdhanSound ? 'bg-[#8B7355]' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-0.5 ${settings.playAdhanSound ? 'translate-x-6' : ''}`} />
                  </button>
                </label>

                {/* Notification permission */}
                {notificationPermission !== 'granted' && (
                  <button
                    onClick={() => setShowNotificationPermissionModal(true)}
                    className="w-full mt-2 py-2.5 bg-[#8B7355] hover:bg-[#6B5744] text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Bell className="w-4 h-4" />
                    Bildirimlere İzin Ver
                  </button>
                )}

                {notificationPermission === 'granted' && (
                  <div className="w-full mt-2 py-2.5 bg-green-600/20 text-green-700 rounded-lg flex items-center justify-center gap-2 border border-green-600/30">
                    <Check className="w-4 h-4" />
                    Bildirimler Aktif - Arka Planda Çalışır
                  </div>
                )}

                {notificationPermission === 'denied' && (
                  <div className="w-full mt-2">
                    <div className="py-2.5 bg-red-600/20 text-red-700 rounded-lg flex items-center justify-center gap-2 border border-red-600/30 text-sm mb-2">
                      <BellOff className="w-4 h-4" />
                      Bildirimler Engellendi
                    </div>
                    <button
                      onClick={() => {
                        // Try requesting permission again
                        handleNotificationPermission(true);
                      }}
                      className="w-full py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium"
                    >
                      🔄 Tekrar Dene
                    </button>
                  </div>
                )}

                {/* Test buttons */}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={testWarningNotification}
                    className="flex-1 py-2.5 bg-[#8B7355] hover:bg-[#6B5744] text-white rounded-lg flex items-center justify-center gap-2 transition-colors font-medium text-sm"
                  >
                    <Clock className="w-4 h-4" />
                    5 dk Uyarı Test
                  </button>
                  <button
                    onClick={testNotification}
                    className="flex-1 py-2.5 bg-[#D5C4A1] hover:bg-[#C4B391] text-[#5C4033] rounded-lg flex items-center justify-center gap-2 transition-colors font-medium text-sm"
                  >
                    <Play className="w-4 h-4" />
                    Ezan Test
                  </button>
                </div>
              </div>

              <p className="text-xs text-[#8B7355] mt-4 text-center">
                Ayarlarınız otomatik olarak kaydedilir
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center text-white py-20">
            <div className="animate-spin w-12 h-12 border-4 border-[#D5C4A1]/30 border-t-[#D5C4A1] rounded-full mx-auto mb-4"></div>
            <p className="text-xl text-[#D5C4A1]">Namaz vakitleri yükleniyor...</p>
          </div>
        ) : (
          <>
            {/* Date and countdown section */}
            <div className="bg-[#F5EFE7]/10 backdrop-blur rounded-xl p-6 mb-6 border border-[#D5C4A1]/20">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                {/* Date info */}
                <div className="text-center lg:text-left">
                  <h2 className="text-2xl font-bold text-[#F5EFE7]">
                    {formatDate(new Date())}
                  </h2>
                  <p className="text-[#D5C4A1] text-lg flex items-center justify-center lg:justify-start gap-2">
                    <MapPin className="w-4 h-4" />
                    {selectedCity.name}, {selectedCountry}
                  </p>
                </div>

                {/* Moon phase */}
                <div className="flex items-center justify-center">{getMoonPhase()}</div>

                {/* Countdown */}
                <div className="text-center">
                  <p className="text-[#D5C4A1] text-sm mb-1">
                    {prayerNames[nextPrayer?.name] || 'Sonraki'} Ezanına Kalan Süre
                  </p>
                  <div className="text-4xl font-bold text-[#F5EFE7] font-mono tracking-wider">
                    {countdown.hours}:{countdown.minutes}:{countdown.seconds}
                  </div>
                </div>

                {/* Current time */}
                <div className="text-center">
                  <p className="text-[#D5C4A1] text-sm mb-1">Saat</p>
                  <div className="text-4xl font-bold text-[#F5EFE7] font-mono">
                    {currentTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            </div>

            {/* Prayer times cards */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
              {['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'].map((prayer) => {
                const isNext = nextPrayer?.name === prayer;
                return (
                  <div
                    key={prayer}
                    className={`rounded-xl p-4 text-center transition-all ${
                      isNext 
                        ? 'bg-[#D5C4A1] text-[#5C4033] shadow-lg scale-105 ring-2 ring-[#F5EFE7]' 
                        : 'bg-[#F5EFE7]/10 text-[#F5EFE7] hover:bg-[#F5EFE7]/20 border border-[#D5C4A1]/20'
                    }`}
                  >
                    <p className={`text-sm font-medium ${isNext ? 'text-[#6B5744]' : 'text-[#D5C4A1]'}`}>
                      {prayerNames[prayer]}
                    </p>
                    <p className={`text-2xl font-bold mt-1 font-mono ${isNext ? 'text-[#5C4033]' : 'text-[#F5EFE7]'}`}>
                      {prayerTimes?.timings?.[prayer] || '--:--'}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Tabs */}
            <div className="bg-[#6B5744] rounded-t-xl overflow-hidden">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('weekly')}
                  className={`flex-1 py-3 px-4 font-medium transition-colors ${
                    activeTab === 'weekly' ? 'bg-[#8B7355] text-white' : 'text-[#D5C4A1] hover:bg-[#8B7355]/50'
                  }`}
                >
                  Haftalık Namaz Vakti
                </button>
                <button
                  onClick={() => setActiveTab('monthly')}
                  className={`flex-1 py-3 px-4 font-medium transition-colors ${
                    activeTab === 'monthly' ? 'bg-[#8B7355] text-white' : 'text-[#D5C4A1] hover:bg-[#8B7355]/50'
                  }`}
                >
                  Aylık Namaz Vakitleri
                </button>
              </div>
            </div>

            {/* Weekly table */}
            <div className="bg-[#F5EFE7] rounded-b-xl overflow-hidden shadow-lg">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-[#5C4033] mb-4">
                  {selectedCity.name} İçin {activeTab === 'weekly' ? 'Haftalık' : 'Aylık'} Namaz Vakitleri
                </h3>
                
                {activeTab === 'weekly' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-[#D5C4A1] bg-[#E8DCC8]">
                          <th className="text-left py-3 px-3 text-[#5C4033] font-semibold">Tarih</th>
                          <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">İmsak</th>
                          <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Güneş</th>
                          <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Öğle</th>
                          <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">İkindi</th>
                          <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Akşam</th>
                          <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Yatsı</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyTimes.map((day, index) => {
                          const isToday = day.date.toDateString() === new Date().toDateString();
                          return (
                            <tr 
                              key={index} 
                              className={`border-b border-[#D5C4A1]/50 ${isToday ? 'bg-[#D5C4A1]/30 font-medium' : 'hover:bg-[#E8DCC8]/50'}`}
                            >
                              <td className="py-3 px-3">
                                <div className="font-medium text-[#5C4033]">
                                  {day.date.getDate()} {months[day.date.getMonth()]} {daysOfWeek[day.date.getDay()]}
                                </div>
                                <div className="text-xs text-[#8B7355]">
                                  {selectedCity.name}, {selectedCountry}
                                </div>
                              </td>
                              <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{day.timings.Fajr}</td>
                              <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{day.timings.Sunrise}</td>
                              <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{day.timings.Dhuhr}</td>
                              <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{day.timings.Asr}</td>
                              <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{day.timings.Maghrib}</td>
                              <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{day.timings.Isha}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Monthly Table */
                  <div className="overflow-x-auto">
                    {loadingMonthly ? (
                      <div className="text-center py-8">
                        <div className="animate-spin w-8 h-8 border-4 border-[#D5C4A1]/30 border-t-[#8B7355] rounded-full mx-auto mb-2"></div>
                        <p className="text-[#8B7355]">Aylık vakitler yükleniyor...</p>
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2 border-[#D5C4A1] bg-[#E8DCC8]">
                            <th className="text-left py-3 px-3 text-[#5C4033] font-semibold">Tarih</th>
                            <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">İmsak</th>
                            <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Güneş</th>
                            <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Öğle</th>
                            <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">İkindi</th>
                            <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Akşam</th>
                            <th className="text-center py-3 px-2 text-[#5C4033] font-semibold">Yatsı</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyTimes.map((day, index) => {
                            const isToday = day.date.toDateString() === new Date().toDateString();
                            return (
                              <tr 
                                key={index} 
                                className={`border-b border-[#D5C4A1]/50 ${isToday ? 'bg-[#D5C4A1]/30 font-medium' : 'hover:bg-[#E8DCC8]/50'}`}
                              >
                                <td className="py-3 px-3">
                                  <div className="font-medium text-[#5C4033]">
                                    {day.date.getDate()} {months[day.date.getMonth()]} {daysOfWeek[day.date.getDay()]}
                                  </div>
                                  <div className="text-xs text-[#8B7355]">
                                    {selectedCity.name}, {selectedCountry}
                                  </div>
                                </td>
                                <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{cleanTimeFormat(day.timings.Fajr)}</td>
                                <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{cleanTimeFormat(day.timings.Sunrise)}</td>
                                <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{cleanTimeFormat(day.timings.Dhuhr)}</td>
                                <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{cleanTimeFormat(day.timings.Asr)}</td>
                                <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{cleanTimeFormat(day.timings.Maghrib)}</td>
                                <td className="py-3 px-2 text-center font-mono text-[#5C4033]">{cleanTimeFormat(day.timings.Isha)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Adhan Modal */}
      {showAdhanModal && currentAdhan && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
          <div className="bg-gradient-to-b from-[#5C4033] to-[#3D2B1F] rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center relative border-2 border-[#D5C4A1]/30">
            <button
              onClick={closeAdhanModal}
              className="absolute top-4 right-4 text-[#D5C4A1] hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#D5C4A1]/20 flex items-center justify-center border-2 border-[#D5C4A1]/50">
              <Volume2 className="w-10 h-10 text-[#D5C4A1] animate-pulse" />
            </div>
            
            <h2 className="text-2xl font-bold text-[#F5EFE7] mb-2">
              {currentAdhan.prayer} Ezanı
            </h2>
            <p className="text-[#D5C4A1] mb-6">
              {currentAdhan.location}
            </p>
            
            <div className="text-5xl mb-6">🕌</div>
            
            <button
              onClick={closeAdhanModal}
              className="w-full bg-[#D5C4A1] text-[#5C4033] font-bold py-3 px-6 rounded-lg hover:bg-[#E8DCC8] transition-colors"
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* 5 Dakika Uyarı Modal - Tema Uyumlu */}
      {showWarningModal && warningPrayer && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-b from-[#5C4033] via-[#4A3428] to-[#3D2B1F] rounded-3xl shadow-2xl w-full max-w-md p-8 text-center relative border-2 border-[#D5C4A1]/50 overflow-hidden">
            
            {/* Decorative Islamic Pattern Background */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-full h-full" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D5C4A1' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
            </div>
            
            {/* Glow Effects */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-[#D5C4A1]/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-[#8B7355]/30 rounded-full blur-2xl"></div>
            
            {/* Close button */}
            <button
              onClick={closeWarningModal}
              className="absolute top-4 right-4 text-[#D5C4A1]/70 hover:text-[#F5EFE7] transition-colors z-20"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Content */}
            <div className="relative z-10">
              
              {/* Mosque Icon with Animation */}
              <div className="relative w-28 h-28 mx-auto mb-6">
                {/* Outer ring animation */}
                <div className="absolute inset-0 rounded-full border-4 border-[#D5C4A1]/30 animate-ping"></div>
                <div className="absolute inset-2 rounded-full border-2 border-[#D5C4A1]/50 animate-pulse"></div>
                {/* Inner circle */}
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-[#D5C4A1]/40 to-[#8B7355]/40 flex items-center justify-center border-2 border-[#D5C4A1]">
                  <span className="text-5xl">🕌</span>
                </div>
              </div>
              
              {/* Time remaining with decorative lines */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#D5C4A1]/50"></div>
                <div className="text-center">
                  <div className="text-6xl font-bold text-[#F5EFE7] font-mono tracking-wider" style={{textShadow: '0 0 20px rgba(213,196,161,0.5)'}}>
                    5:00
                  </div>
                  <p className="text-[#D5C4A1] text-sm font-medium tracking-widest uppercase mt-1">dakika kaldı</p>
                </div>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#D5C4A1]/50"></div>
              </div>
              
              {/* Prayer name */}
              <h2 className="text-3xl font-bold text-[#F5EFE7] mb-2" style={{textShadow: '0 2px 10px rgba(0,0,0,0.3)'}}>
                {warningPrayer.prayer} Ezanı
              </h2>
              
              {/* Location with icon */}
              <div className="inline-flex items-center gap-2 text-[#D5C4A1] mb-6 bg-[#3D2B1F]/50 px-4 py-2 rounded-full border border-[#D5C4A1]/30">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">{warningPrayer.location}</span>
              </div>
              
              {/* Message box */}
              <div className="bg-gradient-to-r from-[#D5C4A1]/10 via-[#D5C4A1]/20 to-[#D5C4A1]/10 rounded-2xl p-5 mb-6 border border-[#D5C4A1]/30 backdrop-blur-sm">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <Bell className="w-5 h-5 text-[#D5C4A1] animate-bounce" />
                  <p className="text-[#F5EFE7] text-lg font-semibold">
                    {warningPrayer.prayer} namazına hazırlanın
                  </p>
                </div>
                <p className="text-[#D5C4A1]/80 text-sm">
                  Abdest alıp namaza hazırlanma zamanı geldi
                </p>
              </div>
              
              {/* Action button */}
              <button
                onClick={closeWarningModal}
                className="w-full bg-gradient-to-r from-[#D5C4A1] to-[#C4B391] text-[#3D2B1F] font-bold py-4 px-8 rounded-xl hover:from-[#E8DCC8] hover:to-[#D5C4A1] transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl border border-[#D5C4A1]/50"
              >
                <span className="flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" />
                  Tamam
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bildirim İzni Modal - Tema Uyumlu */}
      {showNotificationPermissionModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-b from-[#5C4033] via-[#4A3428] to-[#3D2B1F] rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center relative border-2 border-[#D5C4A1]/40 overflow-hidden animate-in fade-in zoom-in duration-300">
            
            {/* Decorative Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-full h-full" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23D5C4A1' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M20 0L0 20h40L20 0zm0 40L0 20h40l-20 20z'/%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
            </div>
            
            {/* Glow Effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-[#D5C4A1]/20 rounded-full blur-3xl"></div>
            
            {/* Content */}
            <div className="relative z-10">
              
              {/* Bell Icon with Animation */}
              <div className="relative w-20 h-20 mx-auto mb-5">
                <div className="absolute inset-0 rounded-full bg-[#D5C4A1]/20 animate-ping"></div>
                <div className="absolute inset-2 rounded-full bg-gradient-to-br from-[#D5C4A1]/30 to-[#8B7355]/30 flex items-center justify-center border-2 border-[#D5C4A1]/60">
                  <Bell className="w-8 h-8 text-[#D5C4A1] animate-bounce" />
                </div>
              </div>
              
              {/* Site URL */}
              <div className="inline-flex items-center gap-2 bg-[#3D2B1F]/70 px-4 py-1.5 rounded-full mb-4 border border-[#D5C4A1]/30">
                <div className="w-2 h-2 rounded-full bg-[#D5C4A1]"></div>
                <span className="text-[#D5C4A1] text-sm font-medium">{window.location.hostname}</span>
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-[#F5EFE7] mb-3">
                Bildirimleri göstermek istiyor
              </h3>
              
              {/* Description */}
              <p className="text-[#D5C4A1]/80 text-sm mb-6 leading-relaxed">
                Namaz vakitlerinden 5 dakika önce hatırlatma bildirimi almak için izin verin.
              </p>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleNotificationPermission(false)}
                  className="flex-1 bg-[#3D2B1F] text-[#D5C4A1] font-semibold py-3 px-4 rounded-xl border border-[#D5C4A1]/30 hover:bg-[#4A3428] hover:border-[#D5C4A1]/50 transition-all"
                >
                  Engelle
                </button>
                <button
                  onClick={() => handleNotificationPermission(true)}
                  className="flex-1 bg-gradient-to-r from-[#D5C4A1] to-[#C4B391] text-[#3D2B1F] font-bold py-3 px-4 rounded-xl hover:from-[#E8DCC8] hover:to-[#D5C4A1] transition-all transform hover:scale-[1.02] shadow-lg"
                >
                  İzin Ver
                </button>
              </div>
              
              {/* Info text */}
              <p className="text-[#D5C4A1]/50 text-xs mt-4">
                Tarayıcı ayarlarından dilediğiniz zaman değiştirebilirsiniz
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bildirim İzni Reddedildi Modal - Tema Uyumlu */}
      {showPermissionDeniedModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-b from-[#5C4033] via-[#4A3428] to-[#3D2B1F] rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center relative border-2 border-[#D5C4A1]/40 overflow-hidden">
            
            {/* Glow Effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
            
            {/* Close button */}
            <button
              onClick={() => setShowPermissionDeniedModal(false)}
              className="absolute top-4 right-4 text-[#D5C4A1]/70 hover:text-[#F5EFE7] transition-colors z-20"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Content */}
            <div className="relative z-10">
              
              {/* Warning Icon */}
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center border-2 border-red-500/50">
                <BellOff className="w-10 h-10 text-red-400" />
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-[#F5EFE7] mb-3">
                Bildirim İzni Reddedildi
              </h3>
              
              {/* Description */}
              <p className="text-[#D5C4A1]/80 text-sm mb-4 leading-relaxed">
                Namaz vakti hatırlatmaları almak için tarayıcı ayarlarından bildirimlere izin vermeniz gerekiyor.
              </p>
              
              {/* Instructions Box */}
              <div className="bg-[#3D2B1F]/70 rounded-xl p-4 mb-5 border border-[#D5C4A1]/20 text-left">
                <p className="text-[#D5C4A1] text-sm font-medium mb-2">📍 İzin vermek için:</p>
                <ol className="text-[#D5C4A1]/70 text-xs space-y-1.5 list-decimal list-inside">
                  <li>Adres çubuğundaki 🔒 kilit ikonuna tıklayın</li>
                  <li>Site ayarları veya İzinler seçeneğini seçin</li>
                  <li>Bildirimler ayarını İzin Ver olarak değiştirin</li>
                  <li>Sayfayı yenileyin</li>
                </ol>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPermissionDeniedModal(false)}
                  className="flex-1 bg-[#3D2B1F] text-[#D5C4A1] font-semibold py-3 px-4 rounded-xl border border-[#D5C4A1]/30 hover:bg-[#4A3428] transition-all"
                >
                  Kapat
                </button>
                <button
                  onClick={() => {
                    setShowPermissionDeniedModal(false);
                    window.location.reload();
                  }}
                  className="flex-1 bg-gradient-to-r from-[#D5C4A1] to-[#C4B391] text-[#3D2B1F] font-bold py-3 px-4 rounded-xl hover:from-[#E8DCC8] hover:to-[#D5C4A1] transition-all"
                >
                  🔄 Yenile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bildirim İzni Başarılı Modal - Tema Uyumlu */}
      {showPermissionSuccessModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-b from-[#5C4033] via-[#4A3428] to-[#3D2B1F] rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center relative border-2 border-[#D5C4A1]/40 overflow-hidden">
            
            {/* Glow Effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-green-500/15 rounded-full blur-3xl"></div>
            
            {/* Content */}
            <div className="relative z-10">
              
              {/* Success Icon */}
              <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-green-500/30 to-green-600/20 flex items-center justify-center border-2 border-green-500/50 animate-pulse">
                <div className="w-16 h-16 rounded-full bg-green-500/30 flex items-center justify-center">
                  <Check className="w-10 h-10 text-green-400" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-[#F5EFE7] mb-2">
                🕌 Bildirimler Aktif!
              </h3>
              
              {/* Subtitle */}
              <p className="text-green-400 text-sm font-medium mb-4">
                Başarıyla etkinleştirildi
              </p>
              
              {/* Description */}
              <div className="bg-[#3D2B1F]/70 rounded-xl p-4 mb-5 border border-green-500/20">
                <p className="text-[#D5C4A1] text-sm leading-relaxed">
                  Artık namaz vakitlerinden <strong className="text-green-400">5 dakika önce</strong> hatırlatma alacaksınız.
                </p>
                <p className="text-[#D5C4A1]/70 text-xs mt-2">
                  Site kapalı olsa bile bildirim gelecek!
                </p>
              </div>
              
              {/* Features List */}
              <div className="flex justify-center gap-4 mb-5">
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-1 rounded-full bg-[#D5C4A1]/20 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-[#D5C4A1]" />
                  </div>
                  <span className="text-[#D5C4A1]/70 text-xs">Uyarı</span>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-1 rounded-full bg-[#D5C4A1]/20 flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-[#D5C4A1]" />
                  </div>
                  <span className="text-[#D5C4A1]/70 text-xs">Ses</span>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-1 rounded-full bg-[#D5C4A1]/20 flex items-center justify-center">
                    <span className="text-lg">🕌</span>
                  </div>
                  <span className="text-[#D5C4A1]/70 text-xs">Ezan</span>
                </div>
              </div>
              
              {/* Button */}
              <button
                onClick={() => setShowPermissionSuccessModal(false)}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-6 rounded-xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-[1.02] shadow-lg"
              >
                <span className="flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" />
                  Harika, Tamam!
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrayerTimesPage;
